import { useEffect, useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { SITE } from "@/config";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import ContactPage from "@/pages/ContactPage";
import DeltaPage from "@/pages/DeltaPage";
import AboutPage from "@/pages/AboutPage";
import FreedomPage from "@/pages/FreedomPage";
import ProjectsPage from "@/pages/ProjectsPage";
import EventsPage from "@/pages/EventsPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import CookiePolicyPage from "@/pages/CookiePolicyPage";

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    if (!hash) {
      window.scrollTo(0, 0);
      window.__lenis?.scrollTo(0, { immediate: true, force: true });
      window.__lenis?.resize();
      return;
    }
    // Hash: attendi che l'elemento esista nel DOM (route appena montata) e riallinea
    // dopo che immagini/animazioni hanno stabilizzato il layout.
    let cancelled = false;
    let rafId;
    const start = performance.now();
    const tryScroll = () => {
      if (cancelled) return;
      const el = document.querySelector(hash);
      if (el) {
        window.__lenis?.resize();
        window.__lenis?.scrollTo(el, { offset: -72, duration: 1.1 });
        setTimeout(() => {
          if (!cancelled && document.contains(el)) {
            window.__lenis?.scrollTo(el, { offset: -72, duration: 0.6 });
          }
        }, 1000);
        return;
      }
      if (performance.now() - start < 3000) rafId = requestAnimationFrame(tryScroll);
    };
    rafId = requestAnimationFrame(tryScroll);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [pathname, hash]);
  return null;
};

const Analytics = () => {
  useEffect(() => {
    const { gaId, metaPixelId } = SITE.analytics;
    if (gaId) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", gaId);
    }
    if (metaPixelId) {
      /* eslint-disable */
      !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
        if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
        t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
      window.fbq("init", metaPixelId);
      window.fbq("track", "PageView");
      /* eslint-enable */
    }
  }, []);
  return null;
};

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollManager />
      <Analytics />
      <div className="grain" aria-hidden="true" />
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/contatti" element={<ContactPage />} />
          <Route path="/delta" element={<DeltaPage />} />
          <Route path="/chi-sono" element={<AboutPage />} />
          <Route path="/bf360" element={<FreedomPage />} />
          <Route path="/progetti" element={<ProjectsPage />} />
          <Route path="/eventi" element={<EventsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/cookie" element={<Navigate to="/cookie-policy" replace />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </main>
      <Footer />
      <Toaster position="bottom-right" />
    </BrowserRouter>
  );
}
